import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  useActivityPubStatus,
  useActivityPubFollowers,
  useActivityPubComments,
  useApproveComment,
  useDeleteComment,
} from '@wordpuppi/shared';
import { Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UnderlineTabs } from '@/components/sub-components/UnderlineTabs';

export const Route = createFileRoute('/sites/$siteSlug/activitypub')({
  component: ActivityPubPage,
});

type TabType = 'followers' | 'comments';

function ActivityPubPage() {
  const { siteSlug } = Route.useParams();
  const [tab, setTab] = useState<TabType>('followers');
  const { data: status, isLoading: statusLoading } = useActivityPubStatus(siteSlug);

  if (statusLoading) return <Skeleton className="h-96 w-full" />;

  if (!status?.enabled) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">Fediverse / ActivityPub</h1>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">
              ActivityPub is not enabled for this site. Turn it on to federate your
              posts and gather followers across the fediverse.
            </p>
            <Link to="/sites/$siteSlug/settings" params={{ siteSlug }}>
              <Button>Enable in Settings</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabs = [
    { value: 'followers' as const, label: 'Followers', count: status.follower_count },
    { value: 'comments' as const, label: 'Comments', count: status.comment_count },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Fediverse / ActivityPub</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {status.follower_count} followers · {status.total_likes} likes ·{' '}
            {status.total_boosts} boosts · {status.comment_count} comments
          </p>
        </div>
        <code className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
          {status.handle}
        </code>
      </div>

      <UnderlineTabs tabs={tabs} activeTab={tab} onTabChange={setTab} />

      {tab === 'followers' ? (
        <FollowersTab siteSlug={siteSlug} />
      ) : (
        <CommentsTab siteSlug={siteSlug} />
      )}
    </div>
  );
}

function FollowersTab({ siteSlug }: { siteSlug: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useActivityPubFollowers(siteSlug, { page, per_page: 50 });

  if (isLoading) return <Skeleton className="h-48 w-full" />;

  if (!data?.data.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No followers yet. Share your fediverse handle to get started.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Actor</TableHead>
              <TableHead>Handle</TableHead>
              <TableHead>Followed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.data.map((follower) => (
              <TableRow key={follower.id}>
                <TableCell>
                  <a
                    href={follower.actor_uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground font-medium hover:underline"
                  >
                    {follower.actor_name || follower.actor_uri}
                  </a>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {follower.actor_handle || '—'}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {follower.accepted_at
                    ? new Date(follower.accepted_at).toLocaleDateString()
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {data.total > 50 && (
        <div className="flex justify-between items-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {Math.ceil(data.total / 50)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page * 50 >= data.total}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function CommentsTab({ siteSlug }: { siteSlug: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useActivityPubComments(siteSlug, { page, per_page: 50 });
  const approveComment = useApproveComment(siteSlug);
  const deleteComment = useDeleteComment(siteSlug);

  if (isLoading) return <Skeleton className="h-48 w-full" />;

  if (!data?.data.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No comments from the fediverse yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {data.data.map((comment) => (
        <Card key={comment.id}>
          <CardContent className="pt-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <a
                    href={comment.actor_uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-foreground hover:underline text-sm"
                  >
                    {comment.actor_name || comment.actor_handle || comment.actor_uri}
                  </a>
                  <span className="text-xs text-muted-foreground">
                    {comment.created_at
                      ? new Date(comment.created_at).toLocaleDateString()
                      : ''}
                  </span>
                  {!comment.approved && (
                    <Badge variant="warning">Pending</Badge>
                  )}
                </div>
                <div
                  className="text-sm prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: comment.content_html }}
                />
              </div>
              <div className="flex items-center gap-1 ml-4">
                {!comment.approved && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => approveComment.mutate(comment.id)}
                    disabled={approveComment.isPending}
                  >
                    Approve
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => deleteComment.mutate(comment.id)}
                  disabled={deleteComment.isPending}
                  title="Delete comment"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {data.total > 50 && (
        <div className="flex justify-between items-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {Math.ceil(data.total / 50)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page * 50 >= data.total}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
